import { ExternalLink } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { ADMIN_BASE_URL, adminHref } from '@/config/config'
import { useAgentRunDetail } from '@/models/agentRunReport'
import { formatBytes, formatCost, formatDateTime, formatLatency, formatTokens } from './format'
import { RunTextBlock } from './RunTextBlock'
import { StatusBadge } from './StatusBadge'

export function RunDetailSheet({ runId, onClose }: { runId: string | null; onClose: () => void }) {
  const { data: run, isLoading } = useAgentRunDetail(runId)

  return (
    <Sheet open={!!runId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="font-mono text-base">{run?.agentSlug ?? 'Agent run'}</SheetTitle>
        </SheetHeader>
        {isLoading || !run ? (
          <div className="flex flex-col gap-4 p-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <div className="flex flex-col gap-5 p-4 pt-0">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={run.status} />
              <Badge variant="outline">{run.model || '—'}</Badge>
              <Badge variant="outline">{run.vendor}</Badge>
              <span className="text-ink-500 text-sm">{formatDateTime(run.dateCreated)}</span>
              <a
                href={adminHref(`${ADMIN_BASE_URL}/insure/agentrun/${run.id}/change/`)}
                target="_blank"
                rel="noreferrer"
                className="text-ink-500 hover:text-ink ml-auto flex items-center gap-1 text-sm"
              >
                Admin <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Fact label="Latency" value={formatLatency(run.latencyMs)} />
              <Fact label="Cost" value={formatCost(run.costUsd)} />
              <Fact label="Tokens" value={run.tokens ? formatTokens(run.tokens) : '—'} />
              <Fact label="Requests" value={String(run.requests || 1)} />
              <Fact label="Input" value={formatTokens(run.inputTokens)} />
              <Fact label="Output" value={formatTokens(run.outputTokens)} />
              <Fact label="Cache read" value={formatTokens(run.cacheReadTokens)} />
              <Fact label="User" value={run.userEmail ?? 'background task'} />
            </dl>

            {run.error && <RunTextBlock label="Error" text={run.error} tone="error" />}
            <RunTextBlock label="Input" text={run.userMessage} />
            <RunTextBlock label="Response" text={run.response} />
            <RunTextBlock
              label={run.traceBytes ? `Trace · ${formatBytes(run.traceBytes)}` : 'Trace'}
              text={run.traceJson}
              collapsed
            />
            <RunTextBlock label="Metadata" text={run.metadataJson} collapsed />
            {run.systemPrompt && <RunTextBlock label="System prompt" text={run.systemPrompt} collapsed />}

            {run.sessionRuns.length > 0 && (
              <section className="flex flex-col gap-2">
                <h4 className="text-sm font-semibold">Rest of this session</h4>
                <ul className="flex flex-col gap-1">
                  {run.sessionRuns.map((sibling) => (
                    <li key={sibling.id} className="flex items-center gap-2 text-sm">
                      <span className="text-ink-400 w-24 shrink-0">
                        {formatDateTime(sibling.dateCreated)}
                      </span>
                      <StatusBadge status={sibling.status} />
                      <span className="text-ink-600 truncate">{sibling.messagePreview}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-ink-500 text-xs">{label}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  )
}
